import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { GET_WORKER_BY_LINE_JOB } from "../graphql/checkin.graphql";

import { useQuery } from "@apollo/client";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { toast } from "react-toastify";
import { useChangeCheck } from "../components/Checkin/changeCheckHook";
import CheckInForm from "../components/Checkin/CheckInForm";
import CheckinList from "../components/Checkin/CheckInList";
import CheckoutModal from "../components/Checkin/CheckoutModal";
import LineInfoSection from "../components/Checkin/LineInfoSection";
import WorkerInfoModal from "../components/Checkin/WorkerInfoModal";
import DynamicHeader from "../components/DynamicHeader/DynamicHeader";
import Layout from "../components/Layout";
import {
  GET_WORKER_BY_UNIQUE_ID,
  LIST_ALL_POSITIONS,
} from "../graphql/workers.graphql";
import { dateToString } from "../utils/date-utils";

import { useCheckLabor } from "../components/Checkin/checkLaborHook";
import { getOptions } from "../utils/array-utils";
import { uniqBy } from "lodash";

const WorkerCheckInPage = () => {
  const onChangeCheck = useChangeCheck();

  const checkLabor = useCheckLabor();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [openModal, setOpenModal] = useState("");

  const [timelog, setTimelog] = useState();

  const [selectedJob, setSelectedJob] = useState();

  const [worker, setWorker] = useState();

  const [selectedWorker, setSelectedWorker] = useState({});

  const [loadWorker] = useLazyQuery(GET_WORKER_BY_UNIQUE_ID, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const [loadWorkesByLineJob, { data, loading, error }] = useLazyQuery(
    GET_WORKER_BY_LINE_JOB,
    {
      fetchPolicy: "network-only",
    }
  );

  const {
    data: positionData,
    loading: loadingPositions,
    error: positionsError,
  } = useQuery(LIST_ALL_POSITIONS);

  const positions = getOptions(
    "comment",
    positionData?.position,
    loadingPositions,
    positionsError,
    ["comment_es", "is_misc", "is_standard", "code", "base_rate"]
  ).map((p) => ({
    ...p,
    label: language == "es" && p.comment_es ? p.comment_es : p.label,
  }));

  useEffect(() => {
    const selectedJob = localStorage.getItem("selectedJob")
      ? JSON.parse(localStorage.getItem("selectedJob"))
      : null;

    if (selectedJob) {
      setSelectedJob(selectedJob);
      reloadWorkers(selectedJob.id);
    }
  }, [reloadWorkers]);

  const rawWorkers =
    loading || error
      ? []
      : data?.line_job_worker.filter(
          (worker) => worker.worker?.timelogs.length > 0
        );
  const sortedWorkerTimelog = uniqBy(rawWorkers, "worker.id");

  const handleValidateId = async (uniqueId, setFieldValue) => {
    const response = await loadWorker({
      variables: {
        unique_access_id: uniqueId,
      },
    });

    let dataWorker = response?.data?.worker[0] || {};
    dataWorker = { ...dataWorker, unique_access_id: uniqueId };

    setWorker(dataWorker);

    return onChangeCheck(selectedJob, dataWorker, setFieldValue, setTimelog);
  };

  const reloadWorkers = useCallback(
    async (job) => {
      job &&
        (await loadWorkesByLineJob({
          variables: {
            line_job_id: job,
          },
        }));
    },
    [loadWorkesByLineJob]
  );

  const onSubmitCheck = async (
    values,
    resetForm,
    setSubmitting,
    checkStatus
  ) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log("🟣 onSubmitCheck CALLED (pages/checkin.js)", {
        checkStatus,
        workerId: worker?.id,
        jobId: selectedJob?.id,
        timestamp: new Date().toISOString()
      });
    }

    if (!selectedJob) {
      toast.error(t("views.checkin.form.selectLine"));
      setSubmitting(false);
      return;
    }

    const after = () => {
      resetForm();
      reloadWorkers(selectedJob?.id);
      setTimelog();
      setWorker();
    };

    checkLabor(
      after,
      worker,
      timelog,
      values,
      setSubmitting,
      checkStatus,
      selectedJob
    );
  };

  return (
    <Layout variant={"gray"} customClasses="d-flex flex-column">
      <DynamicHeader
        isWorkerCheckin
        selectedLocation={selectedJob?.line?.location || {}}
      />
      <div
        className="w-100 d-flex flex-column align-items-center h-100"
        style={{
          overflow: "auto",
        }}
      >
        {" "}
        <WorkerInfoModal
          open={openModal === "DETAIL"}
          toggleModal={() => setOpenModal("")}
          worker={selectedWorker}
          job={selectedJob}
        />
        <CheckoutModal
          open={openModal === "CHECKOUT"}
          line={selectedJob?.line}
          toggleModal={() => {
            setOpenModal("");
            setTimelog(null);
          }}
          onSubmit={(values, resetForm, setSubmitting) =>
            onSubmitCheck(values, resetForm, setSubmitting, "checkout")
          }
        />
        <div style={{ paddingTop: 40, paddingBottom: 40 }}>
          <LineInfoSection
            line={selectedJob}
            onChange={async (job) => {
              setSelectedJob(job);

              await loadWorkesByLineJob({
                variables: {
                  line_job_id: job.id,
                  date: dateToString(new Date()),
                },
              });
            }}
          />
          <CheckInForm
            onSubmit={(values, setSubmitting, resetForm, checkStatus) => {
              if (checkStatus === "checkout") {
                setOpenModal("CHECKOUT");
                setSubmitting(false);
                resetForm();
              } else {
                onSubmitCheck(values, resetForm, setSubmitting, checkStatus);
              }
            }}
            positions={positions}
            line={selectedJob?.line}
            handleValidateId={handleValidateId}
            selectedJob={selectedJob}
            reloadWorkers={reloadWorkers}
          />
          <CheckinList
            loading={loading}
            line={selectedJob?.line}
            workersData={sortedWorkerTimelog}
            toggleModal={(values) => {
              setSelectedWorker(values);
              setOpenModal("DETAIL");
            }}
          />
        </div>
      </div>
    </Layout>
  );
};

export async function getServerSideProps(ctx) {
  return {
    props: {
      ...(await serverSideTranslations(ctx.locale, ["common"])),
    },
  };
}

export default WorkerCheckInPage;
